import { useCallback, useEffect, useRef, useState } from 'react';
import axiosInstant from '../../../../api';
import config from '../../../../config';
import {
  adminAffinitySubProfessionGraph,
  adminAffinityCategoryGraph,
  adminAffinityProfessionGraph,
  adminAffinityProductGraph,
  adminAffinityCategories,
} from '../../../../api/endpoints';
import { AffinityGraphResponse, AffinityCategory } from './affinity.types';

export type AffinityMode = 'sub_profession' | 'category' | 'profession' | 'product';

const endpointFor = (mode: AffinityMode, id: number): string => {
  if (mode === 'category') return adminAffinityCategoryGraph(id);
  if (mode === 'profession') return adminAffinityProfessionGraph(id);
  if (mode === 'product') return adminAffinityProductGraph(id);
  return adminAffinitySubProfessionGraph(id);
};

// Fetches the focused affinity neighborhood for one focus entity (a
// sub-profession, a category, or a whole profession). Returns the raw API
// response (untouched — the graph component clones nodes per render so
// react-force-graph's in-place mutation never corrupts our state).
export function useAffinityGraph(mode: AffinityMode, id: number | null) {
  const [data, setData] = useState<AffinityGraphResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  // Monotonic request sequence: only the latest refetch may commit state.
  // Without it, switching focus while a slow response is in flight lets the
  // OLD entity's graph land under the NEW focus — and edits staged against
  // that stale graph would save links onto the wrong entity.
  const seqRef = useRef(0);

  const refetch = useCallback(async () => {
    const seq = ++seqRef.current;
    if (!id) {
      setData(null);
      return;
    }
    setLoading(true);
    setError(false);
    try {
      const res = await axiosInstant.get(`${config.API_URL}${endpointFor(mode, id)}`);
      if (seq !== seqRef.current) return;
      setData(res.data);
    } catch (err) {
      if (seq !== seqRef.current) return;
      setError(true);
      setData(null);
    } finally {
      if (seq === seqRef.current) setLoading(false);
    }
  }, [mode, id]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { data, loading, error, refetch };
}

// Loads the full flat category list (with derived tier) once, for the add-link
// cascading picker. Cached for the lifetime of the component.
export function useAffinityCategories() {
  const [categories, setCategories] = useState<AffinityCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    axiosInstant
      .get(`${config.API_URL}${adminAffinityCategories}`)
      .then((res) => {
        if (active) setCategories(res.data || []);
      })
      .catch(() => {
        if (active) {
          setCategories([]);
          setError(true);
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  return { categories, loading, error };
}
